import {
  trans,
  getResourcePath,
  useSoyuzData,
  useSoyuzExperiment
} from '@spotahome/soyuz/client';

import {
  Section,
  ThemeProvider,
  CookiesProvider,
  ToastContainer,
  FooterSimplified,
  UserInfoProvider,
  GoogleOneTap,
  AppBanner,
  CitiesProvider,
  HeaderContainer
} from '@spotahome/ui-library';
import ZohoChatbot from '@spotahome/ui-library/src/tenant/components/ZohoChatbot/ZohoChatbot';

// Styles
import '@spotahome/landings-common/src/scss/init.scss';

import LazyLoginSignUpModal from '@spotahome/landings-common/src/components/LoginSignupModal';
import HeroRedesign from '@spotahome/landings-common/src/components/HeroRedesign';

import HiwGuarentee from './components/HiwGuarentee';
import HiwContentRedesign from './components/HiwContentRedesign';
import Hero from './components/Hero';
import HiwSteps from './components/HiwSteps';
import Guarantees from './components/Guarantees';
import Partners from './components/Partners';
import Testimonials from './components/Testimonials';
import HiwComparison from './components/HiwComparison';
import Faqs from './components/Faqs';
import HiwSocial from './components/HiwSocial';

import { getJsonLdHowTo, getJsonLdHowToVariant } from './utils/getJsonLdHowTo';
import { getStructuredFaqs } from './utils/faqs';

const HowItWorksContainer = () => {
  const { [HowItWorksContainer.NAME]: requestedData } = useSoyuzData();
  const { deviceType, deviceViewType, initialCookie, userId } = requestedData;
  const isExperimentVariant =
    useSoyuzExperiment('HowItWorksFocusedOnAI2').get('behaviour') === 'variant';

  return (
    <CookiesProvider initialCookie={initialCookie}>
      <ThemeProvider flags={{ newButtons: true }}>
        <UserInfoProvider>
          <CitiesProvider skipAllCities>
            <ToastContainer />
            {!userId && <GoogleOneTap />}
            <AppBanner source="home" deviceType={deviceType} />
            <ZohoChatbot />

            {isExperimentVariant ? (
              <>
                <HeaderContainer />
                <Hero />
                <HiwSteps />
                <Guarantees />
                <Testimonials />
                <HiwComparison />
                <Partners />
                <Faqs />
                <HiwSocial />
              </>
            ) : (
              <>
                <HeroRedesign
                  tracking="ga-how-it-works"
                  title={trans('how_it_works.alt.hero.title')}
                  backgroundImage={`url(${getResourcePath(
                    '/images/how-it-works/how-it-works.svg'
                  )})`}
                  deviceType={deviceType}
                />

                <Section background="light">
                  <HiwContentRedesign />
                </Section>
                <Section background="skin">
                  <HiwGuarentee />
                </Section>
              </>
            )}
            <FooterSimplified />

            <LazyLoginSignUpModal deviceViewType={deviceViewType} />
          </CitiesProvider>
        </UserInfoProvider>
      </ThemeProvider>
    </CookiesProvider>
  );
};

HowItWorksContainer.NAME = 'HowItWorksContainer';

HowItWorksContainer.requestAllData = async req => {
  const isExperiment =
    req.getExperiment('HowItWorksFocusedOnAI2').get('behaviour') === 'variant';
  const getJsonLd = isExperiment ? getJsonLdHowToVariant : getJsonLdHowTo;

  const faqsJsonLd = isExperiment
    ? {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(getStructuredFaqs(req.trans))
      }
    : {};

  return {
    headers: {
      scripts: [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(getJsonLd(req))
        },
        faqsJsonLd
      ]
    },
    data: {
      deviceType: req.device.type,
      deviceViewType: req.device.viewType,
      initialCookie: req.headers.cookie,
      userId: req.userId
    }
  };
};

export default HowItWorksContainer;
